import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

/** GET /api/auth/me — includes signature references, never raw signature bytes. */
export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  plan: 'free' | 'pro' | 'enterprise';
  role?: 'user' | 'admin';
  avatarUrl?: string;
  signatureId?: string | null;
  initialsId?: string | null;
  signatureVerified?: boolean;
  phone?: string;
  address?: string;
  company?: string;
  jobTitle?: string;
  updatedAt?: string;
  emailVerified?: boolean;
  usage?: { aiRequestsThisMonth: number; pagesThisMonth: number };
}

const LS_LAST_ACTIVE_KEY = 'corner:last-active';

/** Set when local inactivity exceeds `INACTIVITY_TIMEOUT_MS`; App shows welcome/refresher modal once. */
export const IDLE_WELCOME_PENDING_KEY = 'corner:idle-welcome-pending';

/** Auto-logout if inactive for this many ms (30 days). Matches server refresh token TTL. */
const INACTIVITY_TIMEOUT_MS = 30 * 24 * 60 * 60 * 1000;
/** Refresh the access token this many ms before it expires (access token is 15m → refresh at 14m). */
const ACCESS_TOKEN_REFRESH_INTERVAL_MS = 14 * 60 * 1000;

// Refresh token lives in an httpOnly cookie — browser sends it automatically.
// All auth requests need credentials: true so cookies are included cross-origin.
const authAxios = axios.create({ withCredentials: true });

// ─── Token refresh debounce (BoltzmanEngine pattern) ────────────────
// Prevents concurrent refresh calls from causing token rotation conflicts.
// The first caller does the refresh; concurrent callers wait on the same promise.
let refreshPromise: Promise<string | null> | null = null;

interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export interface UseAuthReturn {
  user: AuthUser | null;
  accessToken: string | null;
  loading: boolean;
  /**
   * True once GET /api/auth/me has returned successfully at least once in this session.
   * Distinguishes "profile not yet fetched" from "profile fetched, signatureId is null".
   * OAuthCallbackPage uses this to avoid false-positive signature setup redirects.
   */
  profileHydrated: boolean;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => string | null;
  /** Reload profile from GET /api/auth/me (signature fields, plan, etc.). */
  refreshUser: () => Promise<void>;
  /** Update profile fields via PATCH /api/auth/me. */
  updateProfile: (fields: { displayName?: string; phone?: string; address?: string; company?: string; jobTitle?: string }) => Promise<void>;
}

function recordActivity(): void {
  try {
    localStorage.setItem(LS_LAST_ACTIVE_KEY, Date.now().toString());
  } catch {
    // ignore
  }
}

function isSessionStale(): boolean {
  try {
    const raw = localStorage.getItem(LS_LAST_ACTIVE_KEY);
    if (!raw) return false; // no record → treat as fresh (first-time or migrated)
    const lastActive = parseInt(raw, 10);
    return Date.now() - lastActive > INACTIVITY_TIMEOUT_MS;
  } catch {
    return false;
  }
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileHydrated, setProfileHydrated] = useState(false);

  const accessTokenRef = useRef<string | null>(null);
  accessTokenRef.current = accessToken;

  // Prevents StrictMode double-mount from firing two refresh calls.
  // Token rotation deletes the old token server-side, so a duplicate call
  // with the stale cookie → 401 → session wiped.
  const mountRefreshFiredRef = useRef(false);

  // ----- helpers -----

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setProfileHydrated(false);
    localStorage.removeItem(LS_LAST_ACTIVE_KEY);
  }, []);

  const doRefresh = useCallback(async (): Promise<string | null> => {
    // Debounce: if a refresh is already in-flight, piggyback on it.
    // Prevents token rotation conflicts when multiple callers refresh simultaneously.
    if (refreshPromise) return refreshPromise;

    const promise = (async (): Promise<string | null> => {
      try {
        const res = await authAxios.post<{ accessToken: string }>('/api/auth/refresh');
        const { accessToken: newAccess } = res.data;
        setAccessToken(newAccess);
        recordActivity();
        return newAccess;
      } catch (err) {
        // Only log out when the server says the token is invalid/expired (401).
        // Network errors, 5xx, or DB unavailable (503) should not log the user out.
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === 401) {
          clearSession();
        }
        return null;
      } finally {
        refreshPromise = null;
      }
    })();

    refreshPromise = promise;
    return promise;
  }, [clearSession]);

  const fetchFullProfile = useCallback(async (token: string) => {
    try {
      const meRes = await axios.get<AuthUser>('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(meRes.data);
      setProfileHydrated(true);
    } catch (err) {
      // Keep minimal user from login/register if /me fails.
      // profileHydrated stays false so consumers don't make decisions on incomplete data.
      console.warn('[useAuth] Failed to fetch full profile:', err);
    }
  }, []);

  const applyAuthResponse = useCallback(
    (data: AuthResponse) => {
      setAccessToken(data.accessToken);
      setUser({ ...data.user });
      // Login/register responses include signatureId, so profile is considered hydrated
      setProfileHydrated(true);
      recordActivity();
      void fetchFullProfile(data.accessToken);
    },
    [fetchFullProfile]
  );

  // ----- session restore on mount -----

  useEffect(() => {
    // Guard: React StrictMode double-mount fires this effect twice.
    // With token rotation the second call would send the already-deleted old
    // cookie → 401 → session wiped. The ref survives the synthetic
    // unmount/remount so the duplicate is skipped.
    if (mountRefreshFiredRef.current) {
      setLoading(false);
      return;
    }
    mountRefreshFiredRef.current = true;

    let cancelled = false;

    // If user has been inactive for > INACTIVITY_TIMEOUT_MS, skip restore
    if (isSessionStale()) {
      // Best-effort: tell server to clear the cookie
      authAxios.post('/api/auth/logout').catch(() => {});
      try {
        localStorage.setItem(IDLE_WELCOME_PENDING_KEY, '1');
        localStorage.removeItem(LS_LAST_ACTIVE_KEY);
      } catch {
        /* ignore */
      }
      setLoading(false);
      return;
    }

    authAxios
      .post<{ accessToken: string }>('/api/auth/refresh')
      .then(async (res) => {
        if (cancelled) return;
        const { accessToken: newAccess } = res.data;
        setAccessToken(newAccess);
        recordActivity();

        await fetchFullProfile(newAccess);
      })
      .catch((err) => {
        if (cancelled) return;
        // 401 means no valid cookie → stay logged out (expected for new visitors)
        // Other errors (network, 5xx) → also stay loading=false but don't clear activity
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status !== 401) {
          // non-auth error — don't wipe last-active; user may reconnect
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [fetchFullProfile]);

  const refreshUser = useCallback(async () => {
    const token = accessTokenRef.current ?? (await doRefresh());
    if (!token) return;
    await fetchFullProfile(token);
  }, [doRefresh, fetchFullProfile]);

  // ----- proactive access token refresh timer -----

  useEffect(() => {
    if (!user) return;

    // Refresh the access token every 14 minutes so it never expires mid-session
    const interval = setInterval(() => {
      doRefresh();
    }, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user, doRefresh]);

  // ----- activity tracking -----

  useEffect(() => {
    if (!user) return;

    // Update last-active timestamp on meaningful user events
    const events = ['mousemove', 'keydown', 'pointerdown', 'scroll'] as const;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const onActivity = () => {
      if (debounceTimer) return;
      debounceTimer = setTimeout(() => {
        recordActivity();
        debounceTimer = null;
      }, 60_000); // write at most once per minute
    };

    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [user]);

  // ----- public API -----

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      try {
        const res = await authAxios.post<AuthResponse>('/api/auth/register', {
          email,
          password,
          displayName,
        });
        applyAuthResponse(res.data);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const code = err.response?.data?.code as string | undefined;
          if (code === 'EMAIL_EXISTS') throw new Error('EMAIL_EXISTS');
          const message = err.response?.data?.message as string | undefined;
          throw new Error(message ?? 'Registration failed');
        }
        throw new Error('Registration failed');
      }
    },
    [applyAuthResponse]
  );

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const res = await authAxios.post<AuthResponse>('/api/auth/login', { email, password });
        applyAuthResponse(res.data);
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const code = err.response?.data?.code as string | undefined;
          if (code === 'INVALID_CREDENTIALS' || code === 'USER_NOT_FOUND') throw new Error('INVALID_CREDENTIALS');
          const message = err.response?.data?.message as string | undefined;
          throw new Error(message ?? 'Login failed');
        }
        throw new Error('Login failed');
      }
    },
    [applyAuthResponse]
  );

  const logout = useCallback(async () => {
    // Server clears the httpOnly cookie; we just need to fire the request
    authAxios.post('/api/auth/logout').catch(() => {});
    clearSession();
  }, [clearSession]);

  const getToken = useCallback(() => accessTokenRef.current, []);

  const updateProfile = useCallback(
    async (fields: { displayName?: string; phone?: string; address?: string; company?: string; jobTitle?: string }) => {
      const token = accessTokenRef.current ?? (await doRefresh());
      if (!token) throw new Error('Not authenticated');
      await authAxios.patch('/api/auth/me', fields, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchFullProfile(token);
    },
    [doRefresh, fetchFullProfile]
  );

  return { user, accessToken, loading, profileHydrated, register, login, logout, getToken, refreshUser, updateProfile };
}
